/**
 * ============================================================================
 * [P3-04-E] AddURLForm - URL 추가 폼 컴포넌트
 *
 * 기능:
 * - 모달 형태의 URL 입력 폼
 * - 서비스 명칭, 설명, URL 주소 입력
 * - 폼 유효성 검사
 * ============================================================================
 */

// ============================================================================
// 1. Imports
// ============================================================================
import { useState, useCallback, useRef, useEffect } from 'react'
import type { URLLink } from '@/types'
import styles from './URLLink.module.css'

// ============================================================================
// 2. Types
// ============================================================================
interface AddURLFormProps {
  initialData?: URLLink | null
  onSubmit: (data: Omit<URLLink, 'id' | 'createdAt'>) => void
  onClose: () => void
}

// ============================================================================
// 3. AddURLForm Component
// ============================================================================
export function AddURLForm({ initialData, onSubmit, onClose }: AddURLFormProps) {
  // ========================================================================
  // 3-1. 수정 모드 여부
  // ========================================================================
  const isEditMode = Boolean(initialData)

  // ========================================================================
  // 3-2. 폼 상태 (수정 시 초기값 설정)
  // ========================================================================
  const [name, setName] = useState(initialData?.name || '')
  const [description, setDescription] = useState(initialData?.description || '')
  const [url, setUrl] = useState(initialData?.url || '')

  // ========================================================================
  // 3-3. 첫 번째 입력 필드에 포커스
  // ========================================================================
  const nameInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    nameInputRef.current?.focus()
  }, [])

  // ========================================================================
  // 3-4. ESC 키로 모달 닫기
  // ========================================================================
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // ========================================================================
  // 3-5. 폼 제출 핸들러
  // ========================================================================
  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault()

    // 필수 필드 검증
    if (!name.trim() || !url.trim()) {
      return
    }

    // URL 형식 보정 (http:// 없으면 추가)
    let formattedUrl = url.trim()
    if (!formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
      formattedUrl = `https://${formattedUrl}`
    }

    onSubmit({
      name: name.trim(),
      description: description.trim() || undefined,
      url: formattedUrl,
      order: 0,
    })
  }, [name, description, url, onSubmit])

  // ========================================================================
  // 3-6. Render
  // ========================================================================
  return (
    <div
      className={styles.modalBackdrop}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-url-title"
    >
      <form
        className={styles.addUrlForm}
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        {/* ================================================================
            헤더
            ================================================================ */}
        <h3 id="add-url-title">{isEditMode ? '링크 수정' : '링크 추가'}</h3>

        {/* ================================================================
            서비스 명칭 입력 (필수)
            ================================================================ */}
        <div className={styles.formGroup}>
          <label htmlFor="url-link-name">서비스 명칭 *</label>
          <input
            id="url-link-name"
            ref={nameInputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="예: Google Analytics"
            maxLength={50}
            required
            aria-required="true"
          />
        </div>

        {/* ================================================================
            설명 입력 (선택)
            ================================================================ */}
        <div className={styles.formGroup}>
          <label htmlFor="url-link-desc">설명 (선택)</label>
          <input
            id="url-link-desc"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="예: 마케팅 데이터 분석"
            maxLength={100}
          />
        </div>

        {/* ================================================================
            URL 주소 입력 (필수)
            ================================================================ */}
        <div className={styles.formGroup}>
          <label htmlFor="url-link-url">URL 주소 *</label>
          <input
            id="url-link-url"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com"
            required
            aria-required="true"
          />
        </div>

        {/* ================================================================
            버튼 영역
            ================================================================ */}
        <div className={styles.formActions}>
          <button type="button" onClick={onClose}>
            ❌ 취소
          </button>
          <button type="submit">
            💾 저장
          </button>
        </div>
      </form>
    </div>
  )
}

export default AddURLForm

